import { Alert, Box } from '@mui/material';
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { SensorAlertTypes } from '@/api/customSensorAlerts/types';
import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { SensorType, useDeviceHasSensor } from '@/api/device';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { flattenSensorPositions, linesAcrossSensors } from '@/charts/common';
import { GenericObject } from '@/charts/helpers';
import { AlertLine } from '@/charts/types';
import { SensorIcon, SensorIconProps } from '@/components/data/SensorIcon';
import { ChartSensorSelector } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { alertLineParamsForSensorChart, composeAlertLines } from '@/utils';

export type SensorQueryData<Datum extends GenericObject> = {
    data?: { [sensorPosition: string]: Datum[] };
    isPending: boolean;
    isFetching: boolean;
};

export type SensorLineChartProps<Datum extends GenericObject> = {
    chartId: string;
    chartTitle: string;
    yAxisLabel: string;
    spotterId: string;
    showAlertLines: boolean;
    scaleToFitAlerts: boolean;
    alertLines?: AlertLine[];
    lines: Line<Datum>[];
    dataset: Datum[];
    onSelectData?: (data: LineDataSlice<Datum> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const SensorLineChart = <Datum extends GenericObject>({
    chartId,
    chartTitle,
    yAxisLabel,
    spotterId,
    showAlertLines,
    scaleToFitAlerts,
    alertLines,
    dataset,
    lines,
    onSelectData,
    onSelectTimestamp,
}: SensorLineChartProps<Datum>) => {
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<Datum> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    const timeZone = usePreferredTimeZone();

    const containerRef = useRef<HTMLElement>(null);
    const containerId = chartId + useId();
    const { width, height } = useElementSize(containerRef);

    useTrackChartView({
        containerEl,
        spotterId,
        chartTitle,
    });

    const drawChart = useCallback(drawLineChart, []);

    useEffect(() => {
        if (!dataset) {
            return;
        }

        const newChart = drawChart<Datum>({
            dataset,
            lines,
            xAxisKey: 'timestamp',
            yAxisLabel,
            containerId,
            showAlertLines,
            scaleChartToFitAllAlerts: scaleToFitAlerts,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dataset,
        dateRange,
        timeZone,
        drawChart,
        height,
        lines,
        onSelectData,
        onSelectTimestamp,
        scaleToFitAlerts,
        showAlertLines,
        width,
        yAxisLabel,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [dataset?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export type SensorLineChartSectionProps<Datum extends GenericObject> = {
    chartId: string;
    chartTitle: string;
    yAxisLabel: string;
    sensorType: SensorType;
    sensorData: SensorQueryData<Datum>;
    baseLine: Omit<Line<Datum>, 'color' | 'dataFilter'>;
    sensorIcon: SensorIconProps;
    legend?: ReactNode;
    alertLineId: SensorAlertTypes;
};

export const SensorLineChartSection = <Datum extends GenericObject & { timestamp: string }>({
    chartTitle,
    yAxisLabel,
    chartId,
    sensorType,
    sensorData: { data: sensorData, isPending, isFetching },
    baseLine,
    sensorIcon,
    legend,
    alertLineId,
}: SensorLineChartSectionProps<Datum>) => {
    type PositionDatum = Datum & { sensorPosition: string };
    const { spotterId } = useSpotterContext();
    const { hasSensor } = useDeviceHasSensor({ spotterId, type: sensorType });
    const { dateRange } = useDateRangeContext();
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const [selectedData, setSelectedData] = useState<LineDataSlice<PositionDatum> | null>(null);
    const [activeSensorPositions, setActiveSensorPositions] = useState<string[]>([]);

    const isAlertsDisplayEnabled = isAlertLinesOn || isScaleToFitAlertLinesOn;
    const isAlertsQueryEnabled = isAlertsDisplayEnabled && !!spotterId;
    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertsQueryEnabled },
    });

    const hasData = Object.values(sensorData ?? {}).some((data) => data?.length > 0);

    const { sensorPositions, dataset } = useMemo(() => flattenSensorPositions(sensorData), [sensorData]);

    const alertLines = useMemo(() => {
        return composeAlertLines({
            deviceAlerts,
            triggerTypes: [alertLineId],
            ...alertLineParamsForSensorChart(sensorPositions, activeSensorPositions),
        });
    }, [alertLineId, deviceAlerts, sensorPositions, activeSensorPositions]);

    const lines = useMemo(
        () =>
            linesAcrossSensors([baseLine], sensorPositions) as unknown as (Line<PositionDatum> & {
                sensorPosition: string;
            })[],
        [baseLine, sensorPositions]
    );

    const activeLines = useMemo(
        () => lines.filter((l) => !activeSensorPositions.length || activeSensorPositions.includes(l.sensorPosition)),
        [activeSensorPositions, lines]
    );

    if (!hasData && !hasSensor) {
        return;
    }

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon {...sensorIcon} />}
        >
            {hasData && dataset ? (
                <ChartWithInfo
                    legend={legend}
                    chart={
                        <SensorLineChart
                            chartId={chartId}
                            chartTitle={chartTitle}
                            yAxisLabel={yAxisLabel}
                            spotterId={spotterId!}
                            alertLines={alertLines}
                            showAlertLines={isAlertLinesOn}
                            scaleToFitAlerts={isScaleToFitAlertLinesOn}
                            lines={activeLines}
                            dataset={dataset}
                            onSelectData={setSelectedData}
                            onSelectTimestamp={setSelectedTimestamp}
                        />
                    }
                >
                    <ChartDataSelectors>
                        <ChartSensorSelector sensorPositions={sensorPositions} onChange={setActiveSensorPositions} />
                    </ChartDataSelectors>
                    <ChartDataDetails>
                        {!selectedData && (
                            <LineChartDataSummary
                                dateRange={dateRange}
                                lines={activeLines}
                                dataset={dataset}
                                renderLineSummary={() => (
                                    <>
                                        <DataLineMean />
                                        <DataLineExtent />
                                    </>
                                )}
                            />
                        )}
                        <DataDetailsByTimestamp
                            selectedData={selectedData}
                            datumRenderer={({ lines, datum }) => (
                                <LineChartDatumDetail datum={datum} dataset={dataset} lines={lines} />
                            )}
                        />
                    </ChartDataDetails>
                </ChartWithInfo>
            ) : (
                <Alert severity="info">No data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
